/**
 * Frontend Input Validation & Sanitization Utility
 * Validates user input before sending to backend
 */

// Sanitize text input
export const sanitizeText = (text) => {
  if (typeof text !== "string") return "";
  
  return text
    .trim()
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/<[^>]*>/g, "")
    .substring(0, 500);
};

// Validate email
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate password
export const validatePassword = (password) => {
  if (!password || password.length < 8) {
    return { valid: false, message: "Password must be at least 8 characters" };
  }
  if (password.length > 128) {
    return { valid: false, message: "Password is too long" };
  }
  return { valid: true };
};

// Validate phone number
export const validatePhone = (phone) => {
  if (typeof phone !== "string") return false;
  // Accept exactly 11 digits (0-9) only
  const digitsOnly = phone.replace(/[^0-9]/g, "");
  const phoneRegex = /^[0-9]{11}$/;
  return phoneRegex.test(digitsOnly);
};

// Validate age
export const validateAge = (age) => {
  const num = Number(age);
  return num >= 18 && num <= 100;
};

// Sanitize form data
export const sanitizeFormData = (formData) => {
  const sanitized = { ...formData };
  
  for (const key in sanitized) {
    const value = sanitized[key];
    
    if (typeof value === "string") {
      sanitized[key] = sanitizeText(value);
    }
  }
  
  return sanitized;
};

// Validate registration form
export const validateRegistrationForm = (formData) => {
  const errors = {};
  
  // Validate full name
  if (!formData.fullName || formData.fullName.trim().length < 2) {
    errors.fullName = "Full name must be at least 2 characters";
  }
  
  // Validate email
  if (!formData.email || !validateEmail(formData.email)) {
    errors.email = "Please enter a valid email address";
  }
  
  // Validate password
  const passwordCheck = validatePassword(formData.password);
  if (!passwordCheck.valid) {
    errors.password = passwordCheck.message;
  }
  
  // Validate password confirmation
  if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = "Passwords do not match";
  }
  
  // Validate contact number if provided
  if (formData.contactNumber && !validatePhone(formData.contactNumber)) {
    errors.contactNumber = "Please enter a valid phone number";
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Validate profile form
export const validateProfileForm = (formData) => {
  const errors = {};
  
  // Validate required fields
  if (!formData.fullName || formData.fullName.trim().length < 2) {
    errors.fullName = "Full name is required";
  }
  
  if (!formData.gender) {
    errors.gender = "Gender is required";
  }
  
  if (!formData.age || !validateAge(formData.age)) {
    errors.age = "Age must be between 18 and 100";
  }
  
  if (!formData.city || formData.city.trim().length < 2) {
    errors.city = "City is required";
  }
  
  if (formData.contactNumber && !validatePhone(formData.contactNumber)) {
    errors.contactNumber = "Please enter a valid phone number";
  }
  
  if (formData.email && !validateEmail(formData.email)) {
    errors.email = "Please enter a valid email address";
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Prevent XSS in display
export const escapeHTML = (text) => {
  if (typeof text !== "string") return "";
  
  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  
  return text.replace(/[&<>"']/g, (char) => map[char]);
};

